// src/components/commissioner/CommissionerCashTools.js
import React, { useState, useEffect } from 'react';
import { DollarSign, Plus, Minus, AlertCircle, Check, X, TrendingUp, TrendingDown } from 'lucide-react';
import { dynastyTheme } from '../../services/colorService';
import { cashAPI } from '../../services/apiService';

const CommissionerCashTools = ({ leagueId }) => {
  const [teams, setTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [adjustmentAmount, setAdjustmentAmount] = useState('');
  const [adjustmentType, setAdjustmentType] = useState('add'); // 'add' or 'subtract'
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  useEffect(() => {
    fetchTeams();
  }, [leagueId]);

  const fetchTeams = async () => {
    try {
      setLoading(true);
      const response = await cashAPI.getLeagueTeamBalances(leagueId);
      
      if (response.success) {
        setTeams(response.teams);
      }
    } catch (err) {
      console.error('Error fetching teams:', err);
      setError('Failed to load team data');
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validation
    if (!selectedTeamId) {
      setError('Please select a team');
      setTimeout(() => setError(''), 3000);
      return;
    }

    const amount = parseFloat(adjustmentAmount);
    if (isNaN(amount) || amount <= 0) {
      setError('Please enter a valid amount greater than 0');
      setTimeout(() => setError(''), 3000);
      return;
    }

    if (!description.trim()) {
      setError('Please provide a description for this adjustment');
      setTimeout(() => setError(''), 3000);
      return;
    }

    // Show confirmation modal
    setShowConfirmModal(true);
  };

  const executeAdjustment = async () => {
    try {
      setSubmitting(true);
      setError('');
      setMessage('');

      const amount = parseFloat(adjustmentAmount);
      const finalAmount = adjustmentType === 'add' ? amount : -amount;

      const response = await cashAPI.manualCashAdjustment(
        leagueId,
        selectedTeamId,
        finalAmount,
        description.trim()
      );

      if (response.success) {
        setMessage(`Successfully ${adjustmentType === 'add' ? 'added' : 'subtracted'} $${amount.toFixed(2)}`);
        
        // Reset form
        setAdjustmentAmount('');
        setDescription('');
        setShowConfirmModal(false);
        
        // Refresh teams to show new balance
        await fetchTeams();
        
        setTimeout(() => setMessage(''), 5000);
      } else {
        throw new Error(response.message || 'Failed to process adjustment');
      }
    } catch (err) {
      console.error('Error processing adjustment:', err);
      setError(err.message || 'Failed to process cash adjustment');
      setShowConfirmModal(false);
      setTimeout(() => setError(''), 5000);
    } finally {
      setSubmitting(false);
    }
  };

  const getSelectedTeam = () => {
    return teams.find(t => t.team_id === parseInt(selectedTeamId));
  };

  const getNewBalance = () => {
    const team = getSelectedTeam();
    if (!team || !adjustmentAmount) return null;
    
    const amount = parseFloat(adjustmentAmount);
    if (isNaN(amount)) return null;
    
    const currentBalance = parseFloat(team.cash_balance) || 0;
    const finalAmount = adjustmentType === 'add' ? amount : -amount;
    return currentBalance + finalAmount;
  };

  if (loading) {
    return (
      <div className={dynastyTheme.components.card.base}>
        <div className="p-6 text-center">
          <div className="inline-block w-8 h-8 border-4 border-gray-600 border-t-yellow-400 rounded-full animate-spin"></div>
          <p className={`mt-4 ${dynastyTheme.classes.text.neutralLight}`}>Loading team data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className={dynastyTheme.components.card.base}>
        <div className="p-6">
          <h2 className={`${dynastyTheme.components.heading.h2} flex items-center gap-2`}>
            <DollarSign className="w-6 h-6 text-green-400" />
            Manual Cash Adjustments
          </h2>
          <p className={dynastyTheme.classes.text.neutralLight}>
            Add or subtract cash from team balances for commissioner corrections or league events
          </p>
        </div>
      </div>

      {/* Status Messages */}
      {message && (
        <div className={`p-4 rounded-lg ${dynastyTheme.classes.bg.success}/20 text-green-400 flex items-center gap-2`}>
          <Check className="w-5 h-5" />
          {message}
        </div>
      )}
      
      {error && (
        <div className={`p-4 rounded-lg ${dynastyTheme.classes.bg.error}/20 text-red-400 flex items-center gap-2`}>
          <AlertCircle className="w-5 h-5" />
          {error}
        </div>
      )}

      {/* Team Balances Overview */}
      <div className={dynastyTheme.components.card.base}>
        <div className="p-6">
          <h3 className={dynastyTheme.components.heading.h3}>Current Team Balances</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
            {teams.map(team => (
              <div
                key={team.team_id}
                className={`p-4 rounded-lg ${dynastyTheme.classes.bg.darkLighter} border ${
                  selectedTeamId === team.team_id.toString()
                    ? 'border-yellow-400'
                    : dynastyTheme.classes.border.neutral
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className={`text-sm ${dynastyTheme.classes.text.white} font-medium`}>
                      {team.team_name}
                    </div>
                    <div className={`text-xs ${dynastyTheme.classes.text.neutralLight}`}>
                      {team.manager_name}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`text-lg font-bold ${
                      parseFloat(team.cash_balance) < 0 ? 'text-red-400' : 'text-green-400'
                    }`}>
                      ${parseFloat(team.cash_balance).toFixed(2)}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Adjustment Form */}
      <div className={dynastyTheme.components.card.base}>
        <div className="p-6">
          <h3 className={dynastyTheme.components.heading.h3}>Make Adjustment</h3>
          
          <form onSubmit={handleSubmit} className="mt-4 space-y-4">
            {/* Team Selection */}
            <div>
              <label className={`block text-sm font-medium ${dynastyTheme.classes.text.white} mb-2`}>
                Select Team
              </label>
              <select
                value={selectedTeamId}
                onChange={(e) => setSelectedTeamId(e.target.value)}
                className={`w-full px-4 py-2 rounded-lg ${dynastyTheme.classes.bg.darkLighter} ${dynastyTheme.classes.text.white} border ${dynastyTheme.classes.border.neutral} focus:outline-none focus:border-yellow-400`}
              >
                <option value="">-- Select a team --</option>
                {teams.map(team => (
                  <option key={team.team_id} value={team.team_id}>
                    {team.team_name} (Current: ${parseFloat(team.cash_balance).toFixed(2)})
                  </option>
                ))}
              </select>
            </div>

            {/* Adjustment Type */}
            <div>
              <label className={`block text-sm font-medium ${dynastyTheme.classes.text.white} mb-2`}>
                Adjustment Type
              </label>
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => setAdjustmentType('add')}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 ${dynastyTheme.classes.transition} ${
                    adjustmentType === 'add'
                      ? 'border-green-400 bg-green-400/20 text-green-400'
                      : `border-gray-600 ${dynastyTheme.classes.text.neutralLight} hover:border-green-400/50`
                  }`}
                >
                  <Plus className="w-5 h-5" />
                  <span className="font-medium">Add Cash</span>
                </button>
                <button
                  type="button"
                  onClick={() => setAdjustmentType('subtract')}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 ${dynastyTheme.classes.transition} ${
                    adjustmentType === 'subtract'
                      ? 'border-red-400 bg-red-400/20 text-red-400'
                      : `border-gray-600 ${dynastyTheme.classes.text.neutralLight} hover:border-red-400/50`
                  }`}
                >
                  <Minus className="w-5 h-5" />
                  <span className="font-medium">Subtract Cash</span>
                </button>
              </div>
            </div>

            {/* Amount */}
            <div>
              <label className={`block text-sm font-medium ${dynastyTheme.classes.text.white} mb-2`}>
                Amount ($)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={adjustmentAmount}
                onChange={(e) => setAdjustmentAmount(e.target.value)}
                placeholder="0.00"
                className={`w-full px-4 py-2 rounded-lg ${dynastyTheme.classes.bg.darkLighter} ${dynastyTheme.classes.text.white} border ${dynastyTheme.classes.border.neutral} focus:outline-none focus:border-yellow-400`}
              />
            </div>

            {/* Description */}
            <div>
              <label className={`block text-sm font-medium ${dynastyTheme.classes.text.white} mb-2`}>
                Description/Reason
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="e.g., League fee payment, compensation for error, prize money, etc."
                rows={3}
                className={`w-full px-4 py-2 rounded-lg ${dynastyTheme.classes.bg.darkLighter} ${dynastyTheme.classes.text.white} border ${dynastyTheme.classes.border.neutral} focus:outline-none focus:border-yellow-400 resize-none`}
              />
            </div>

            {/* Preview */}
            {selectedTeamId && adjustmentAmount && getNewBalance() !== null && (
              <div className={`p-4 rounded-lg ${dynastyTheme.classes.bg.darkLighter} border-2 ${
                adjustmentType === 'add' ? 'border-green-400/30' : 'border-red-400/30'
              }`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className={`text-xs ${dynastyTheme.classes.text.neutralLight} mb-1`}>
                      New Balance Preview
                    </div>
                    <div className={`text-sm ${dynastyTheme.classes.text.white} font-medium`}>
                      {getSelectedTeam()?.team_name}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-2">
                      <span className={dynastyTheme.classes.text.neutralLight}>
                        ${parseFloat(getSelectedTeam()?.cash_balance).toFixed(2)}
                      </span>
                      {adjustmentType === 'add' ? (
                        <TrendingUp className="w-4 h-4 text-green-400" />
                      ) : (
                        <TrendingDown className="w-4 h-4 text-red-400" />
                      )}
                      <span className={`text-lg font-bold ${
                        getNewBalance() < 0 ? 'text-red-400' : 'text-green-400'
                      }`}>
                        ${getNewBalance().toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={submitting || !selectedTeamId || !adjustmentAmount || !description.trim()}
              className={`w-full px-6 py-3 rounded-lg font-bold ${dynastyTheme.classes.transition} ${
                submitting || !selectedTeamId || !adjustmentAmount || !description.trim()
                  ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                  : adjustmentType === 'add'
                  ? 'bg-green-600 hover:bg-green-500 text-white'
                  : 'bg-red-600 hover:bg-red-500 text-white'
              }`}
            >
              {submitting ? 'Processing...' : `${adjustmentType === 'add' ? 'Add' : 'Subtract'} Cash`}
            </button>
          </form>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`${dynastyTheme.components.card.base} max-w-md w-full`}>
            <h3 className={`${dynastyTheme.components.heading.h3} flex items-center gap-2 text-${adjustmentType === 'add' ? 'green' : 'red'}-400`}>
              <AlertCircle className={`w-6 h-6 text-${adjustmentType === 'add' ? 'green' : 'red'}-400`} />
              Confirm Cash Adjustment
            </h3>

            <div className={`p-4 rounded-lg ${dynastyTheme.classes.bg.darkLighter} mb-4`}>
              <div className={`text-sm ${dynastyTheme.classes.text.neutralLight} mb-2`}>
                You are about to:
              </div>
              <div className={`text-base ${dynastyTheme.classes.text.white} font-semibold mb-1`}>
                {adjustmentType === 'add' ? 'Add' : 'Subtract'} ${parseFloat(adjustmentAmount).toFixed(2)}
              </div>
              <div className={`text-sm ${dynastyTheme.classes.text.neutralLight} mb-2`}>
                {adjustmentType === 'add' ? 'to' : 'from'} {getSelectedTeam()?.team_name}
              </div>
              <div className={`text-xs ${dynastyTheme.classes.text.neutralLight} mt-3 pt-3 border-t ${dynastyTheme.classes.border.neutral}`}>
                <strong>Reason:</strong> {description}
              </div>
            </div>

            <div className={`text-xs ${dynastyTheme.classes.text.neutralLight} mb-4 italic`}>
              This action will be recorded in the team's cash ledger and cannot be undone.
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirmModal(false)}
                disabled={submitting}
                className={`flex-1 px-4 py-2 rounded-lg font-medium ${dynastyTheme.classes.bg.darkLighter} ${dynastyTheme.classes.text.white} hover:bg-gray-600 ${dynastyTheme.classes.transition}`}
              >
                Cancel
              </button>
              <button
                onClick={executeAdjustment}
                disabled={submitting}
                className={`flex-1 px-4 py-2 rounded-lg font-bold ${dynastyTheme.classes.transition} ${
                  submitting
                    ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                    : adjustmentType === 'add'
                    ? 'bg-green-600 hover:bg-green-500 text-white'
                    : 'bg-red-600 hover:bg-red-500 text-white'
                }`}
              >
                {submitting ? 'Processing...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommissionerCashTools;
